/**
 * Composable for checking if an email already has an account.
 * Used on sign-up forms to warn existing users on blur, before they fill out the whole form.
 */
export function useEmailExistsCheck() {
  const config = useRuntimeConfig();
  const emailExists = ref(false);
  const checking = ref(false);
  let lastCheckedEmail = '';

  const checkEmail = async (email: string) => {
    const trimmed = email?.trim()?.toLowerCase();

    // Skip if empty, invalid, or same as last check
    if (!trimmed || !/.+@.+\..+/.test(trimmed) || trimmed === lastCheckedEmail) {
      return;
    }

    lastCheckedEmail = trimmed;
    checking.value = true;
    emailExists.value = false;

    try {
      const apiBase = config.public.apiBase;
      if (!apiBase) return;

      const result = await $fetch<{ exists: boolean }>(`${apiBase}/api/v1/auth/check-email`, {
        method: 'POST',
        body: { email: trimmed }
      });

      // Only update if this is still the latest check
      if (trimmed === lastCheckedEmail) {
        emailExists.value = result.exists;
      }
    } catch {
      // Silently fail — don't block sign-up flow on check errors
      emailExists.value = false;
    } finally {
      checking.value = false;
    }
  };

  const reset = () => {
    emailExists.value = false;
    lastCheckedEmail = '';
  };

  return { emailExists, checking, checkEmail, reset };
}
